import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { siteConfig } from "@/config";

interface LoadingScreenProps {
  progress: number;
  isLoaded: boolean;
}

export const LoadingScreen = ({ progress, isLoaded }: LoadingScreenProps) => {
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (isLoaded) {
      // Add a small delay before hiding the loading screen for smoother transition
      const timer = setTimeout(() => {
        setIsHidden(true);
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [isLoaded]);

  return (
    <AnimatePresence>
      {!isHidden && (
        <motion.div
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1],
            },
          }}
        >
          <motion.div
            className="flex flex-col items-center justify-center gap-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            {/* Logo animation */}
            <motion.div
              className="relative flex items-center justify-center w-24 h-24"
              animate={{ rotate: isLoaded ? 180 : 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.div
                className="absolute inset-0 border-t-2 border-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  ease: "linear",
                }}
              />
              <div className="font-display text-4xl tracking-wider">
                {siteConfig.name.split(" ")[0][0]}
                {siteConfig.name.split(" ")[1][0]}
              </div>
            </motion.div>

            {/* Progress bar */}
            <div className="w-[280px] h-[2px] bg-white/20 overflow-hidden">
              <motion.div
                className="h-full bg-white"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{
                  duration: 0.4,
                  ease: [0.16, 1, 0.3, 1],
                }}
              />
            </div>

            {/* Progress percentage */}
            <motion.div
              className="text-sm font-light text-white/60"
              key={progress}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {progress}% loaded
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
